/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useEffect } from 'react'
import { FormattedDate } from 'react-intl'
import { useSearchParams } from 'react-router-dom'

import {
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiIcon,
  EuiSkeletonText,
  EuiLoadingSpinner,
  EuiPagination,
  EuiSpacer,
  EuiText,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { CuiTable } from '@modules/cui/Table'
import type { CuiTableColumn } from '@modules/cui/Table'
import PrivacySensitiveContainer from '@modules/cui/PrivacySensitiveContainer'
import { useSearchDeploymentEsResourceLogsQuery } from '@modules/cloud-lib/deployments/hooks'
import { CuiAlert } from '@modules/cui/Alert'
import type { ApiErrorCollection } from '@modules/query/types'
import { useConfig } from '@modules/cui/ConfigContext'

import { getFirstEsClusterFromGet } from '@/lib/stackDeployments/selectors/fundamentals'

import Monitoring from '../../../../../components/StackDeployments/Monitoring'
import { numericDateTime } from '../../../../../config/dates'
import { startPageActions } from '../../../../../../modules/utils/apm'

import Search from './DeploymentLogsSearch'
import { mapFields } from './helpers'

import type { ElasticsearchLogMessage } from './helpers'
import type { AllProps } from './types'

// ENUM from the backend: https://github.com/elastic/cloud/blob/2f3262160494f746d4a24cec56fd567f7de780d6/scala-services/adminconsole/src/main/scala/no/found/adminconsole/api/v1/routes/deployments/logs/LogsController.scala#L50-L56
const LOG_LEVELS = {
  FATAL: 0,
  ERROR: 3,
  WARN: 4,
  INFO: 6,
  DEBUG: 7,
}

const DEFAULT_OFFSET = 0
const DEFAULT_LIMIT = 20

const DeploymentLogs: React.FunctionComponent<AllProps> = ({ deployment }) => {
  const [params, setParams] = useSearchParams()
  const pollingInterval = useConfig('POLLING_INTERVAL')

  useEffect(() => {
    startPageActions(`Cluster logs`)
  }, [])

  const offset = parseInt(params.get('offset')!, 10) || DEFAULT_OFFSET // eslint-disable-line @typescript-eslint/no-non-null-assertion
  const limit = parseInt(params.get('limit')!, 10) || DEFAULT_LIMIT // eslint-disable-line @typescript-eslint/no-non-null-assertion
  const filter = params.getAll('filter')
  const q = params.get('q') ?? undefined

  const deploymentId = deployment?.id

  const cluster = deployment && getFirstEsClusterFromGet({ deployment })

  const clusterId = cluster?.id

  const refId = cluster?.ref_id
  const regionId = cluster?.region

  const { data, isLoading, isFetching, error } = useSearchDeploymentEsResourceLogsQuery(
    {
      deploymentId: deploymentId ?? '', // sanity, will be disabled if absent
      refId: refId ?? '', // sanity, will be disabled if absent
      offset,
      limit,
      filter: filter.length > 0 ? filter.join(`,`) : undefined,
      q: q || undefined,
    },
    {
      enabled: Boolean(deploymentId) && Boolean(refId),
      staleTime: pollingInterval,
      refetchInterval: pollingInterval,
      keepPreviousData: true,
    },
  )

  if (!deploymentId || !clusterId || !regionId) {
    return null
  }

  const total = data?.total

  const { settings } = deployment
  const isLogsMonitoring = settings?.observability?.logging

  return (
    <Fragment>
      <Monitoring
        deployment={deployment}
        deploymentId={deployment.id}
        regionId={regionId}
        clusterId={clusterId}
      />

      <EuiSpacer size='xxl' />
      {isLogsMonitoring ? renderMonitoringMessage() : renderLogs()}
    </Fragment>
  )

  function renderMonitoringMessage() {
    return (
      <Fragment>
        <EuiHorizontalRule margin='l' />

        <EuiSpacer />

        <EuiFlexGroup
          gutterSize='s'
          alignItems='center'
          justifyContent='spaceAround'
          direction='column'
        >
          <EuiFlexItem grow={false}>
            <EuiIcon type='logsApp' size='xl' />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiText color='subdued' size='xs'>
              <FormattedMessage
                id='deployment-logs.view-logs-message'
                defaultMessage='View logs via your chosen deployment'
              />
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      </Fragment>
    )
  }

  function renderLogs() {
    return (
      <Fragment>
        <Search query={q} onSearch={setLogsQuery} />
        <EuiSpacer />
        {renderResults()}
      </Fragment>
    )
  }

  function renderResults() {
    if (isLoading) {
      return <EuiSkeletonText lines={1} />
    }

    if (error) {
      return <CuiAlert type='error'>{(error as ApiErrorCollection).errors[0]?.message}</CuiAlert>
    }

    if (!total) {
      return (
        <EuiFlexGroup gutterSize='m' alignItems='center' responsive={false}>
          <EuiFlexItem grow={false}>
            <FormattedMessage id='deployment-logs.no-results' defaultMessage='No results' />
          </EuiFlexItem>
          {isFetching && (
            <EuiFlexItem grow={false}>
              <EuiLoadingSpinner size='m' />
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      )
    }

    const tableColumns: Array<CuiTableColumn<ElasticsearchLogMessage>> = [
      {
        label: (
          <FormattedMessage id='deployment-logs.headings.timestamp' defaultMessage='Timestamp' />
        ),
        render: ({ timestamp }) => <FormattedDate value={timestamp} {...numericDateTime} />,
        width: '250px',
      },
      {
        label: <FormattedMessage id='deployment-logs.headings.level' defaultMessage='Level' />,
        render: ({ level }) => (
          <EuiButtonEmpty
            data-test-subj='levelFilterButton'
            flush='left'
            onClick={() => {
              const selectedLevel = LOG_LEVELS[level]

              if (selectedLevel) {
                toggleLogsFilter(`level:${selectedLevel}`)
              }
            }}
          >
            {level}
          </EuiButtonEmpty>
        ),
        width: '100px',
      },
      {
        label: (
          <FormattedMessage
            id='deployment-logs.headings.instance-zone'
            defaultMessage='Instance / Zone'
          />
        ),
        render: ({ instanceName, instanceId, isTiebreaker, zone }) => (
          <EuiButtonEmpty
            data-test-id='instanceNameFilterButton'
            flush='left'
            onClick={() => {
              if (zone.length > 0) {
                toggleLogsFilter(`instance_name:${instanceName}`)
              }
            }}
          >
            {`i${instanceId}${!isTiebreaker ? `@${zone}` : ``}`}
          </EuiButtonEmpty>
        ),
        width: '150px',
      },
      {
        label: <FormattedMessage id='deployment-logs.headings.message' defaultMessage='Message' />,
        render: ({ shortMessage }) => (
          <PrivacySensitiveContainer>{shortMessage}</PrivacySensitiveContainer>
        ),
      },
    ]

    return (
      <Fragment>
        <EuiText color='subdued' size='s'>
          <FormattedMessage
            id='deployment-logs.showing-results'
            defaultMessage='Showing {start, number} - {end, number} of {total, number} logs from the last 24 hours.'
            values={{
              start: total > 0 ? offset + 1 : 0,
              end: Math.min(offset + limit, total),
              total,
            }}
          />
          {` `}
          {isFetching && <EuiLoadingSpinner size='m' />}
        </EuiText>

        <EuiSpacer size='m' />

        <CuiTable rows={data.logs.map(mapFields)} columns={tableColumns} />

        <EuiSpacer size='m' />

        {renderPagination()}
      </Fragment>
    )
  }

  function renderPagination() {
    if (!total) {
      return null
    }

    const pageCount = Math.ceil(total / limit)
    const activePage = Math.floor(offset / limit)

    return (
      <EuiFlexGroup>
        <EuiFlexItem grow={false}>
          <EuiPagination
            key={activePage + `-` + pageCount}
            pageCount={pageCount}
            activePage={activePage}
            onPageClick={setLogsPage}
          />
        </EuiFlexItem>

        <EuiFlexItem>{isFetching && <EuiLoadingSpinner size='m' />}</EuiFlexItem>
      </EuiFlexGroup>
    )
  }

  function setLogsPage(page: number) {
    setParams({ offset: (limit * page).toString() }, { replace: true, preventScrollReset: true })
  }

  function setLogsQuery(value: string) {
    setParams({ q: value, offset: '0' }, { replace: true, preventScrollReset: true })
  }

  function toggleLogsFilter(item: string) {
    const newFilter = filter.includes(item) ? filter.filter((x) => x !== item) : filter.concat(item)
    setParams({ filter: newFilter, offset: '0' }, { replace: true, preventScrollReset: true })
  }
}

export default DeploymentLogs
